import React, { useState, useEffect } from 'react';
import { IoChevronBackSharp, IoChevronForwardSharp } from "react-icons/io5"
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { showSuccess, showError } from '../../../../helpers/toast';
import { notifyApiError } from '../../../../helpers/apiNotify';
import { saveDataPdfThenStatus } from '../../../../helpers/applicationSaveFlow';
import { createNotification } from "../../../../store/reducers/notificationSlice";
import { prepareHtmlForPdf } from '../../../../utility/utility';

const FormHeader = (props) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState([]);
    const [inputFields, setInputFields] = useState({});
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const application = useSelector((state) => state.applications.application);
    const pageIds = props.pageIds || [];
    const userRole = props.userRole || user?.user?.user_role;
    const appState = props.applicationState || application?.state;

    // Company admin submits to GB from in-review / company discrepancy screens.
    // Hide only for terminal or already-with-GB states (not a narrow allow-list,
    // so a missing/slow applicationState does not hide the action).
    const blockedFromSubmitToGB = [
        "in_process",
        "approved",
        "rejected",
        "drafts",
        "draft",
        "pending",
        "pending_for_approval",
        "descrepency_GB_GB",
        "descrepency_GB_Employee",
    ];

    const canSubmitToGB =
        !props.viewOnly &&
        props.displayInReview &&
        userRole === "company_admin" &&
        !blockedFromSubmitToGB.includes(appState);

    const canAddDiscrepancies =
        !props.viewOnly &&
        userRole === "company_admin" &&
        appState === "application_submitted_by_company_user_to_company";

    useEffect(() => {
        setInputFields(application?.json_data || {});
    }, [application]);

    const handleGoPrev = (event) => {
        props.previousPage(event);
    }

    const handleGoForw = (event) => {
        props.nextPage(event);
    }

    const handleDownload = async (item) => {
        const applicationId = item.applicationId || props.applicationId;
        const userId = item.userId || props.userId || user.user?.id;
        if (!applicationId) {
            showError('Application ID is missing');
            return;
        }
        if (!userId) {
            showError('User ID is missing');
            return;
        }

        if (isDownloading || downloadingIds.includes(applicationId)) {
            return;
        }

        setDownloadingIds((prev) => [...prev, applicationId]);
        setIsDownloading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/zip",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `application_${applicationId}_files.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showSuccess('Files downloaded successfully!');

        } catch (error) {
            showError(`Download failed: ${error.message}`);
        } finally {
            setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
            setIsDownloading(false);
        }
    };

    const handleSaveApplication = async () => {
        let htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
        try {
            if (props.saveChanges) {
                await props.saveChanges();
            }
            await saveDataPdfThenStatus(dispatch, {
                id: application.id || props.applicationId || params.id,
                token: authToken,
                userId: application.user_id,
                json_data: props?.json_data,
                form: props.applicationName || application?.name,
                currentState: application?.state || appState,
                htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                pdfUser: { id: application.user_id },
                successMessage: 'Application saved successfully!',
            });
        } catch (error) {
            notifyApiError(error, 'Error saving application. Please try again.');
        }
    };

    const handleSubmitToGB = async () => {
        let htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
        const currentAppState = application?.state || appState;
        const newState = currentAppState === "descrepency_GB_Company" ? "descrepency_GB_GB" : "in_process";

        try {
            if (props.saveChanges) {
                await props.saveChanges();
            }

            const result = await saveDataPdfThenStatus(dispatch, {
                id: props.applicationId,
                token: authToken,
                userId: user.user.id,
                json_data: props?.json_data,
                form: props.applicationName,
                currentState: currentAppState,
                nextState: newState,
                htmlForm: htmlForm
                    ? prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds)
                    : null,
                pdfUser: { id: application.user_id },
                successMessage: 'Application submitted to Gaming Board successfully!',
            });

            if (!result.ok) return;

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || props.userId,
                applicationId: props.applicationId,
                applicationName: props.applicationName,
                status: newState,
                accessToken: authToken,
                sendToGamingBoardAdmins: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name
            }));
            navigate(
                newState === "descrepency_GB_GB" ? "/discrepancies-gb" : "/in-process-applications"
            );
            if (props.refreshApplications) {
                props.refreshApplications();
            }
        } catch (error) {
            notifyApiError(error, 'Failed to submit application to Gaming Board');
        }
    };

    const handleAssignToEmployee = async () => {
        try {
            const currentAppState = application?.state || appState;
            const newState = currentAppState === "descrepency_GB_Company"
                ? "descrepency_GB_Employee"
                : "dicrepancy_application_assigned_to_comapany_user";

            const result = await saveDataPdfThenStatus(dispatch, {
                id: props.applicationId,
                token: authToken,
                userId: user.user.id,
                json_data: props?.json_data,
                form: props.applicationName,
                currentState: currentAppState,
                nextState: newState,
                successMessage: 'Application assigned to employee successfully!',
            });

            if (!result.ok) return;

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || props.userId,
                applicationId: props.applicationId,
                applicationName: props.applicationName,
                status: newState,
                accessToken: authToken,
                sendToCompanyAdmins: true,
                sendToCompanyUser: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name
            }));

            navigate('/discrepancies-company');
        }
        catch (error) {
            notifyApiError(error, 'Failed to assign application to employee');
        }
    };

    const handleSaveDraft = async () => {
        try {
            const currentAppState = application?.state || appState;
            const result = await saveDataPdfThenStatus(dispatch, {
                id: props.applicationId,
                token: authToken,
                userId: user.user.id,
                json_data: props?.json_data,
                form: props.applicationName,
                currentState: currentAppState,
                nextState: "drafts",
                successMessage: 'Application saved to drafts successfully!',
            });
            if (!result.ok) return;
            navigate('/draft');
        }
        catch (error) {
            notifyApiError(error, 'Failed to save application to drafts');
        }
    };

    const handleAssignToDiscrepancies = async () => {
        let htmlForm = document.getElementById("DocumentPDF")?.outerHTML;
        const currentAppState = application?.state || appState;
        const finalState = "application_sent_to_discrepancy_by_company";

        try {
            const result = await saveDataPdfThenStatus(dispatch, {
                id: props.applicationId,
                token: authToken,
                userId: user.user.id,
                json_data: props?.json_data,
                form: props.applicationName,
                currentState: currentAppState,
                nextState: finalState,
                htmlForm: htmlForm
                    ? prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds)
                    : null,
                pdfUser: { id: application.user_id },
                successMessage: 'Application assigned to discrepancies successfully!',
            });

            if (!result.ok) return;

            const navId = props.applicationId;
            if (navId) {
                navigate(`/view-discrepancies/company/${navId}`);
            }
            if (props.refreshApplications) {
                props.refreshApplications();
            }
        } catch (error) {
            notifyApiError(error, 'Failed to assign application to discrepancies');
        }
    };

    return (
        <>
            <div className='AppFormBar noShadow'>
                <div className='title'>
                    <div style={{
                        background: "#323639",
                        border: "solid 0px #ccc",
                        borderRadius: "5px",
                        padding: "5px",
                        fontSize: "14px",
                        lineHeight: "22px",
                        color: "#fff",
                    }}>
                        {props.applicationName}
                    </div>
                </div>

                <div className='pagination'>
                    <div className='pageNum d-flex align-items-center'>
                        <IoChevronBackSharp onClick={handleGoPrev} />

                        <input
                            type="number"
                            value={props.currentPage || 1}
                            onChange={props.handlePageChange ? (e) => props.handlePageChange(Number(e.target.value)) : undefined}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.target.select()}
                            readOnly={!props.handlePageChange}
                            style={{
                                width: 'max-content',
                                padding: '0',
                                background: '#191B1C',
                                color: 'white',
                                borderRadius: '5px',
                                textAlign: 'center',
                                WebkitAppearance: 'none',
                                border: 'none',
                            }}
                            min="1"
                            max={Number(props.totalPages)}
                        />

                        <span className='slash'>{`/`}<span style={{ marginLeft: 10 }}></span></span>{props.totalPages}
                        <IoChevronForwardSharp onClick={handleGoForw} />
                    </div>
                    {!props.displayInReview && (
                        <div className='d-flex gap-2'>
                            <button
                                className='btn draft'
                                type='button'
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                    if (props.saveToDrafts) props.saveToDrafts("drafts");
                                }}
                            >
                                Save to Draft
                            </button>
                            <button
                                className='btn'
                                type='button'
                                onClick={(ev) => {
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                    if (props.handleSubmitWithLoading) props.handleSubmitWithLoading();
                                }}
                            >
                                Submit
                            </button>
                        </div>
                    )}
                    {props.displayInReview && (
                        <div className="boardActions d-flex align-items-center gap-2">
                            <div className="dropdown d-inline-flex">

                                <button
                                    className="btn dropdown-toggle "
                                    type="button"
                                    id="dropdownMenuButton"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    View Actions
                                </button>

                                <ul
                                    className="dropdown-menu p-2"
                                    aria-labelledby="dropdownMenuButton"
                                >
                                    <li>
                                        <button
                                            onClick={() => {
                                                if (!isDownloading && !downloadingIds.includes(props.applicationId || application?.id)) {
                                                    handleDownload(props);
                                                }
                                            }}
                                            className="dropdown-item"
                                            style={{
                                                opacity: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 0.6 : 1,
                                                pointerEvents: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'none' : 'auto',
                                                cursor: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'not-allowed' : 'pointer',
                                                background: 'none',
                                                border: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 16px'
                                            }}
                                            disabled={isDownloading || downloadingIds.includes(props.applicationId || application?.id)}
                                        >
                                            {isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'Downloading...' : 'Download'}
                                        </button>
                                    </li>
                                    {canSubmitToGB && (
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                onClick={handleSubmitToGB}
                                            >
                                                Submit to GB
                                            </button>
                                        </li>
                                    )}
                                    {canAddDiscrepancies && (
                                        <li>
                                            <button
                                                type="button"
                                                onClick={handleAssignToDiscrepancies}
                                                className="dropdown-item"
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '8px 16px'
                                                }}
                                            >
                                                Add to Discrepancies
                                            </button>
                                        </li>
                                    )}
                                    {!props.viewOnly && (
                                    <li>
                                        <button
                                            onClick={handleSaveApplication}
                                            className="dropdown-item"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 16px'
                                            }}
                                        >
                                            Save Application
                                        </button>
                                    </li>
                                    )}
                                    {!props.viewOnly &&
                                        userRole === "company_admin" &&
                                        appState !== "application_submitted_by_company_user_to_company" && (
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                onClick={handleAssignToEmployee}
                                            >
                                                Assign to Employee
                                            </button>
                                        </li>
                                    )}

                                    {!props.viewOnly && userRole === "employee" && appState === "application_submitted_by_company_user_to_company" && (
                                        <li>
                                            <button
                                                type="button"
                                                className="dropdown-item"
                                                style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '8px 16px' }}
                                                onClick={handleSaveDraft}
                                            >
                                                Save as Draft
                                            </button>
                                        </li>
                                    )}
                                    {!props.viewOnly && userRole === "employee" && (
                                        <li>
                                            <Link onClick={() => props.handleSubmitWithLoading && props.handleSubmitWithLoading()} className="dropdown-item">
                                                Assign to Company
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default FormHeader;
